import { useEffect, useState } from "react";
import { Download, AlertTriangle } from "lucide-react";
import api, { downloadFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";

export const PiiAccessReport = () => {
  const [days, setDays] = useState("30");
  const [actor, setActor] = useState("all");
  const [docType, setDocType] = useState("all");
  const [report, setReport] = useState(null);

  useEffect(() => {
    const params = { days };
    if (actor !== "all") params.actor = actor;
    if (docType !== "all") params.doc_type = docType;
    api.get("/security/pii-report", { params }).then((r) => setReport(r.data));
  }, [days, actor, docType]);

  if (!report) return <Skeleton className="h-96 w-full rounded-none" />;

  return (
    <div className="space-y-5" data-testid="pii-access-report">
      <div className="flex items-center gap-3 flex-wrap">
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="rounded-none w-32 h-8 text-[12px] bg-white" data-testid="pii-days-select"><SelectValue /></SelectTrigger>
          <SelectContent className="rounded-none">
            {[["7", "Last 7 days"], ["30", "Last 30 days"], ["90", "Last 90 days"]].map(([v, l]) => (
              <SelectItem key={v} value={v} className="text-[12px]">{l}</SelectItem>))}
          </SelectContent>
        </Select>
        <Select value={actor} onValueChange={setActor}>
          <SelectTrigger className="rounded-none w-52 h-8 text-[12px] bg-white" data-testid="pii-actor-select"><SelectValue /></SelectTrigger>
          <SelectContent className="rounded-none">
            <SelectItem value="all" className="text-[12px]">All users</SelectItem>
            {report.by_user.map((u) => (
              <SelectItem key={u.actor_email} value={u.actor_email} className="text-[12px]">{u.actor_email}</SelectItem>))}
          </SelectContent>
        </Select>
        <Select value={docType} onValueChange={setDocType}>
          <SelectTrigger className="rounded-none w-44 h-8 text-[12px] bg-white" data-testid="pii-doctype-select"><SelectValue /></SelectTrigger>
          <SelectContent className="rounded-none">
            <SelectItem value="all" className="text-[12px]">All doc types</SelectItem>
            {Object.keys(report.by_doc_type).map((dt) => (
              <SelectItem key={dt} value={dt} className="text-[12px]">{dt}</SelectItem>))}
          </SelectContent>
        </Select>
        <Button data-testid="pii-report-pdf-btn" variant="outline"
          onClick={() => downloadFile(`/security/pii-report/pdf?days=${days}`, "pii-access-report.pdf")}
          className="rounded-none h-8 text-[11px] ml-auto border-teal-600/50 text-teal-700 hover:bg-teal-50">
          <Download className="h-3.5 w-3.5" /> Export PDF
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[["Total reveals", report.total_reveals, "pii-kpi-total"],
          ["Distinct users", report.by_user.length, "pii-kpi-users"],
          ["Doc types touched", Object.keys(report.by_doc_type).length, "pii-kpi-doctypes"],
          ["Anomaly findings", report.anomalies.length, "pii-kpi-anomalies"]].map(([l, v, t]) => (
          <div key={l} className="bg-white border border-border px-4 py-3" data-testid={t}>
            <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">{l}</p>
            <p className="text-2xl font-bold text-[var(--brand-primary)] mt-1 font-mono">{v}</p>
          </div>
        ))}
      </div>

      {report.anomalies.length > 0 && (
        <div className="bg-amber-50 border border-amber-400" data-testid="pii-anomalies">
          {report.anomalies.map((a, i) => (
            <div key={i} className="flex items-center gap-2.5 px-4 py-2.5 border-b border-amber-200 last:border-0">
              <AlertTriangle className="h-4 w-4 text-amber-700 shrink-0" />
              <p className="text-[12.5px] text-amber-900">
                <span className="font-mono font-semibold">{a.actor_email}</span> — {a.finding} ({a.date})
              </p>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        <div className="bg-white border border-border" data-testid="pii-by-user-table">
          <div className="px-4 py-2.5 border-b border-border">
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Reveals by user</p>
          </div>
          <table className="w-full text-[12.5px]">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border">
                {["User", "Role", "Reveals"].map((h) => <th key={h} className="px-4 py-2 font-semibold">{h}</th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {report.by_user.map((u) => (
                <tr key={u.actor_email} data-testid={`pii-user-row-${u.actor_email}`}>
                  <td className="px-4 py-2.5 font-mono">{u.actor_email}</td>
                  <td className="px-4 py-2.5 text-slate-600">{u.actor_role}</td>
                  <td className="px-4 py-2.5 font-mono font-semibold">{u.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="bg-white border border-border p-4" data-testid="pii-trend-chart">
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground mb-3">Daily reveal trend</p>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={report.daily_trend} margin={{ left: -24 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#64748B" }} />
              <YAxis tick={{ fontSize: 10, fill: "#64748B" }} allowDecimals={false} />
              <Tooltip cursor={{ fill: "#F1F5F9" }} contentStyle={{ borderRadius: 0, fontSize: 12 }} />
              <Bar dataKey="count" fill="var(--brand-accent)" maxBarSize={32} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-white border border-border overflow-x-auto" data-testid="pii-events-table">
        <div className="px-4 py-2.5 border-b border-border">
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
            Recent reveal events — values shown masked only
          </p>
        </div>
        <table className="w-full text-[12px] min-w-[760px]">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border">
              {["Timestamp (UTC)", "User", "Role", "Doc type", "Employee", "Masked value"].map((h) => (
                <th key={h} className="px-4 py-2 font-semibold">{h}</th>))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {report.events.map((e, i) => (
              <tr key={i}>
                <td className="px-4 py-2 font-mono text-slate-500">{String(e.timestamp).slice(0, 16).replace("T", " ")}</td>
                <td className="px-4 py-2 font-mono">{e.actor_email}</td>
                <td className="px-4 py-2 text-slate-600">{e.actor_role}</td>
                <td className="px-4 py-2 text-slate-600">{e.doc_type}</td>
                <td className="px-4 py-2 font-mono">{e.target_employee_id}</td>
                <td className="px-4 py-2 font-mono text-slate-500">{e.masked_value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PiiAccessReport;
